import React, { useCallback, useEffect, useRef, useState } from 'react';
import { checkPushNotificationSupport } from '../utils/notificationUtils';
import {
  getVapidKey,
  inspectSubscription,
  repairSubscription,
  subscribeToPush
} from '../utils/pushSubscription';
import '../styles/PushOptInPrompt.css';

// "No thanks" is permanent (per vendor). "I'll do this later" only lasts the
// session, so the card comes back next time the app is opened.
const DISMISS_KEY = (subUrl) => `push-optin-dismissed:${subUrl}`;
const SNOOZE_KEY = (subUrl) => `push-optin-snoozed:${subUrl}`;
const SYNCED_KEY = (subUrl) => `push-optin-synced:${subUrl}`;

const SHOW_DELAY_MS = 2500;

// Storage can throw in private-browsing modes - never let that break the page.
const safeGet = (store, key) => {
  try {
    return window[store].getItem(key);
  } catch (e) {
    return null;
  }
};
const safeSet = (store, key, value) => {
  try {
    window[store].setItem(key, value);
  } catch (e) {
    /* ignore */
  }
};

/**
 * Asks logged-in customers who have not enabled push notifications whether they
 * would like them, on the vendor's home screen.
 *
 * Note on vendor scoping: a push subscription is stored per user/device, not per
 * vendor - `push_subscriptions` has no vendor_id. Vendor targeting happens at
 * send time by joining through enrollments. So one subscription covers every
 * vendor the customer is enrolled with, and we only prompt people with no
 * subscription at all. Dismissals, however, are remembered per vendor.
 */
function PushOptInPrompt({ vendor, subUrl, isLoggedIn, isActive, iosPwaHandler }) {
  // hidden | prompt | working | success | error
  const [phase, setPhase] = useState('hidden');
  const [message, setMessage] = useState(null);
  const cancelledRef = useRef(false);

  // iosPwaHandler.isIOS is true only for iOS Safari that is NOT installed to the
  // Home Screen - i.e. exactly the case where push cannot work yet.
  const needsIOSInstall = Boolean(iosPwaHandler?.isIOS);
  const support = checkPushNotificationSupport(iosPwaHandler);
  const vapidKey = getVapidKey();

  const close = useCallback(() => {
    setPhase('hidden');
    setMessage(null);
  }, []);

  const handleNoThanks = useCallback(() => {
    safeSet('localStorage', DISMISS_KEY(subUrl), '1');
    close();
  }, [subUrl, close]);

  const handleLater = useCallback(() => {
    safeSet('sessionStorage', SNOOZE_KEY(subUrl), '1');
    close();
  }, [subUrl, close]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Decide whether to show the card.
  useEffect(() => {
    if (!isActive || !isLoggedIn || !subUrl) return undefined;
    if (safeGet('localStorage', DISMISS_KEY(subUrl))) return undefined;
    if (safeGet('sessionStorage', SNOOZE_KEY(subUrl))) return undefined;

    let timer = null;
    let cancelled = false;

    // iOS Safari without the app installed: push is impossible until they add
    // it to the Home Screen, so show the install variant instead of hiding.
    if (needsIOSInstall) {
      timer = setTimeout(() => {
        if (!cancelled) setPhase('prompt');
      }, SHOW_DELAY_MS);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    if (!support.isSupported) return undefined;
    if (typeof Notification === 'undefined') return undefined;
    // Once blocked, the browser will not ask again - prompting is a dead end.
    if (Notification.permission === 'denied') return undefined;
    if (!vapidKey) {
      console.warn('[PushOptIn] REACT_APP_VAPID_PUBLIC_KEY is not set - prompt suppressed');
      return undefined;
    }

    (async () => {
      try {
        const { subscription: existing } = await inspectSubscription();
        if (cancelled) return;

        if (existing) {
          // Already subscribed - but it may have quietly died (expired, rotated
          // VAPID key, or dropped server-side after a 410). Repair it silently,
          // once per session. This never prompts.
          if (!safeGet('sessionStorage', SYNCED_KEY(subUrl))) {
            safeSet('sessionStorage', SYNCED_KEY(subUrl), '1');
            const { state } = await repairSubscription();
            if (cancelled) return;

            // If repair found nothing usable, fall through and ask.
            if (state === 'none') {
              timer = setTimeout(() => {
                if (!cancelled) setPhase('prompt');
              }, SHOW_DELAY_MS);
            }
          }
          return;
        }

        timer = setTimeout(() => {
          if (!cancelled) setPhase('prompt');
        }, SHOW_DELAY_MS);
      } catch (err) {
        console.error('[PushOptIn] Could not check subscription status:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isActive, isLoggedIn, subUrl, needsIOSInstall, support.isSupported, vapidKey]);

  const handleSubscribe = async () => {
    setPhase('working');
    setMessage(null);

    const { state } = await subscribeToPush();
    if (cancelledRef.current) return;

    if (state === 'subscribed') {
      safeSet('sessionStorage', SYNCED_KEY(subUrl), '1');
      setMessage("You're all set. We'll keep you posted.");
      setPhase('success');
      setTimeout(() => {
        if (!cancelledRef.current) close();
      }, 2200);
      return;
    }

    if (state === 'denied') {
      // The browser will not ask again - remember it so we stop trying.
      safeSet('localStorage', DISMISS_KEY(subUrl), '1');
      setMessage('Notifications are blocked. You can turn them back on in your browser settings.');
      setPhase('error');
      return;
    }

    if (state === 'dismissed') {
      // Dialog closed without choosing - treat as "later", not "no".
      handleLater();
      return;
    }

    setMessage('Something went wrong turning on notifications. Please try again later.');
    setPhase('error');
  };

  if (phase === 'hidden') return null;

  const businessName = vendor?.business_name || 'this business';
  const installSteps = needsIOSInstall ? iosPwaHandler?.showInstallInstructions?.() : null;

  return (
    <div className="push-optin-backdrop" role="presentation">
      <div
        className="push-optin-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-optin-title"
      >
        <div className="push-optin-icon" aria-hidden="true">🔔</div>

        {needsIOSInstall ? (
          <>
            <h3 id="push-optin-title">Add the app to get updates</h3>
            <p className="push-optin-body">
              To receive notifications from {businessName} on your iPhone, add this app to your
              Home Screen first.
            </p>
            <ol className="push-optin-steps">
              {(installSteps?.steps || [
                'Tap the Share button in your browser (□↑)',
                'Scroll down and tap "Add to Home Screen"'
              ]).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <div className="push-optin-actions">
              <button type="button" className="push-optin-btn ghost" onClick={handleLater}>
                I'll do this later
              </button>
              <button type="button" className="push-optin-btn link" onClick={handleNoThanks}>
                No thanks
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="push-optin-title">Stay in the loop</h3>
            <p className="push-optin-body">
              Get notified about rewards, specials and news from {businessName}.
            </p>

            {message && (
              <p className={phase === 'success' ? 'push-optin-success' : 'push-optin-error'}>
                {message}
              </p>
            )}

            {phase !== 'success' && (
              <div className="push-optin-actions">
                <button
                  type="button"
                  className="push-optin-btn primary"
                  onClick={handleSubscribe}
                  disabled={phase === 'working'}
                >
                  {phase === 'working' ? 'Just a moment…' : 'Yes, notify me'}
                </button>
                <button
                  type="button"
                  className="push-optin-btn ghost"
                  onClick={handleLater}
                  disabled={phase === 'working'}
                >
                  I'll do this later
                </button>
                <button
                  type="button"
                  className="push-optin-btn link"
                  onClick={handleNoThanks}
                  disabled={phase === 'working'}
                >
                  No thanks
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PushOptInPrompt;
